/**
 * Map Type Control for switching between map types
 */
export class MapTypeControl {
	constructor(mapProvider, mapElement, options = {}) {
		this.mapProvider = mapProvider;
		this.mapElement = mapElement;
		this.options = {
			initialType: 'roadmap',
			types: ['roadmap', 'satellite', 'terrain', 'hybrid'],
			...options
		};
		this.currentType = this.options.initialType;
		this.button = null;
		this.dropdown = null;
		this.isOpen = false;
	}

	init() {
		const zoomControl = this.mapElement.querySelector('.leaflet-control-zoom');
		if (!zoomControl) return;

		this.button = this.createButton();
		this.dropdown = this.createDropdown();

		const fullWindowButton = zoomControl.querySelector('.leaflet-control-fullwindow');
		if (fullWindowButton) {
			zoomControl.insertBefore(this.button, fullWindowButton);
		} else {
			zoomControl.appendChild(this.button);
		}

		// Wrap dropdown in a container that's NOT a child of zoomControl
		// This keeps the maptype button as the last-child so it gets rounded corners
		const dropdownWrapper = document.createElement('div');
		dropdownWrapper.className = 'leaflet-maptype-dropdown-wrapper';
		dropdownWrapper.style.cssText = 'position: absolute; pointer-events: none;';
		dropdownWrapper.appendChild(this.dropdown);

		zoomControl.style.position = 'relative';
		// Append wrapper to map element instead of zoom control
		this.mapElement.appendChild(dropdownWrapper);

		document.addEventListener('click', (e) => {
			if (!this.button.contains(e.target) && !this.dropdown.contains(e.target)) {
				this.closeDropdown();
			}
		});
	}

	createButton() {
		const button = document.createElement('a');
		button.className = 'leaflet-control-maptype';
		button.href = '#';
		button.title = 'Change Map Type';

		button.style.cssText = `
			display: flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			text-decoration: none;
		`;

		button.innerHTML = this.getIcon(this.currentType);

		button.addEventListener('click', (e) => {
			e.preventDefault();
			this.toggleDropdown();
		});



		return button;
	}

	createDropdown() {
		const dropdown = document.createElement('div');
		dropdown.className = 'leaflet-maptype-dropdown position-absolute bg-body border rounded shadow-sm overflow-hidden';
		dropdown.style.cssText = `
			left: 45px;
			top: 58px;
			min-width: 150px;
			z-index: 1000;
			display: none;
			pointer-events: auto;
		`;

		this.options.types.forEach(type => {
			const option = document.createElement('div');
			option.className = 'leaflet-maptype-option d-flex align-items-center gap-2 px-3 py-2 cursor-pointer';
			option.dataset.type = type;

			if (type === this.currentType) {
				option.classList.add('bg-primary-subtle', 'fw-bold');
			}

			const iconWrapper = document.createElement('span');
			iconWrapper.className = 'd-inline-flex flex-shrink-0';
			iconWrapper.style.cssText = 'width: 20px; height: 20px;';
			iconWrapper.innerHTML = this.getIcon(type);

			const label = document.createElement('span');
			label.textContent = this.getLabel(type);
			label.className = 'flex-grow-1';

			option.appendChild(iconWrapper);
			option.appendChild(label);

			option.addEventListener('click', () => {
				this.switchMapType(type);
			});

			option.addEventListener('mouseenter', () => {
				if (type !== this.currentType) {
					option.classList.add('bg-body-secondary');
				}
			});
			option.addEventListener('mouseleave', () => {
				if (type !== this.currentType) {
					option.classList.remove('bg-body-secondary');
				}
			});

			dropdown.appendChild(option);
		});

		return dropdown;
	}

	toggleDropdown() {
		if (this.isOpen) {
			this.closeDropdown();
		} else {
			this.openDropdown();
		}
	}

	openDropdown() {
		this.dropdown.style.display = 'block';
		this.isOpen = true;
	}

	closeDropdown() {
		this.dropdown.style.display = 'none';
		this.isOpen = false;
	}

	switchMapType(type) {
		if (this.mapProvider.setMapType) {
			const success = this.mapProvider.setMapType(type);

			if (success) {
				this.currentType = type;
				this.updateButtonIcon(type);
				this.updateActiveOption(type);
				this.closeDropdown();

				// Update data-maptype attribute to uppercase format for consistency
				this.mapElement.setAttribute('data-maptype', type.toUpperCase());
			}
		}
	}

	updateButtonIcon(type) {
		this.button.innerHTML = this.getIcon(type);
		this.button.title = `Map Type: ${this.getLabel(type)}`;
	}

	updateActiveOption(type) {
		const options = this.dropdown.querySelectorAll('.leaflet-maptype-option');
		options.forEach(option => {
			if (option.dataset.type === type) {
				option.classList.add('bg-primary-subtle', 'fw-bold');
			} else {
				option.classList.remove('bg-primary-subtle', 'fw-bold');
			}
		});
	}

	getIcon(type) {
		const icons = {
			roadmap: `<svg viewBox="0 0 24 24" fill="currentColor" style="width: 18px; height: 18px;">
				<path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z"/>
			</svg>`,
			satellite: `<svg viewBox="0 0 24 24" fill="currentColor" style="width: 18px; height: 18px;">
				<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
			</svg>`,
			terrain: `<svg viewBox="0 0 24 24" fill="currentColor" style="width: 18px; height: 18px;">
				<path d="M14 6l-3.75 5 2.85 3.8-1.6 1.2C9.81 13.75 7 10 7 10l-6 8h22L14 6z"/>
			</svg>`,
			hybrid: `<svg viewBox="0 0 24 24" fill="currentColor" style="width: 18px; height: 18px;">
				<path d="M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z"/>
			</svg>`
		};

		return icons[type] || icons.roadmap;
	}

	getLabel(type) {
		const labels = {
			roadmap: 'Road Map',
			satellite: 'Satellite',
			terrain: 'Terrain',
			hybrid: 'Hybrid'
		};

		return labels[type] || type;
	}
}
